'use client'

import { BenefitsTableBlock } from 'cms-types'
import { useEffect, useMemo } from 'react'
import { BenefitsTable as BenefitsTableUI, type LoadableComponentProps } from 'ui'

import { getCurrentAndNextTier } from '@/cms/helpers'
import { ClubMembershipStatus, useClubMembership } from '@/hooks/useClubMembership'
import { useCustomer } from '@/store/customerStore'
import { useGlobalTypography } from '@/store/globalTypographyStore'
import { useMetaObjects } from '@/store/metaObjectStore'

function renderRemainingPointsTextTemplate(
  template: string,
  remainingPoints: number,
  tier: string,
) {
  return template.replace('{{points}}', String(remainingPoints)).replace('{{tier}}', tier)
}

export function BenefitsTableClient({ data, loading }: LoadableComponentProps<BenefitsTableBlock>) {
  const customer = useCustomer()
  const membership = useClubMembership()
  const tierDefinitions = useMetaObjects(s => s.tierDefinitions)
  const getTierDefinitions = useMetaObjects(s => s.getTierDefinitions)
  const loadingMetaObjects = useMetaObjects(s => s.isLoading)
  const { clubTiers } = useGlobalTypography()

  useEffect(() => {
    getTierDefinitions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const points = customer.customer?.points ?? 0

  const { currentTier, nextTier } = useMemo(
    () => getCurrentAndNextTier(tierDefinitions, points),
    [tierDefinitions, points],
  )

  const remainingPoints = nextTier ? Math.max(nextTier.minimumPoints - points, 0) : 0
  // Members only; empty for non-members and while membership resolves.
  if (membership !== ClubMembershipStatus.Member) return null
  if (loading || loadingMetaObjects) return <BenefitsTableUI loading />

  const columns = data.columns.map(col => ({
    key: col.key,
    header: col.header,
    featured: col.key.toLowerCase() === currentTier?.handle.toLowerCase(),
  }))

  const rows =
    data.rows?.map(row => {
      const normalized: Record<string, { text?: string | null; icon?: string | null }> = {}

      for (const col of data.columns) {
        const key = col.key
        normalized[key] = row?.[key] ?? {}
      }

      return normalized
    }) ?? []

  const nextTierName = nextTier
    ? (clubTiers?.[nextTier.handle as keyof typeof clubTiers] ?? nextTier.name)
    : ''

  return (
    <BenefitsTableUI
      data={{
        ...data,
        table: { columns, rows },
        remainingPointsText: nextTier
          ? renderRemainingPointsTextTemplate(
              data?.remainingPointsText || '',
              remainingPoints,
              nextTierName,
            )
          : undefined,
      }}
    />
  )
}
